// =============================================================================
// Main Application Component
// =============================================================================

import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Import contexts
import { useAuth } from './contexts/AuthContext';
import { useTheme } from './contexts/ThemeContext';

// Import pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CreateWorkspacePage from './pages/CreateWorkspacePage';
import WorkspaceDetailPage from './pages/WorkspaceDetailPage';
import TerminalTestPage from './pages/TerminalTestPage';
import TerminalTypesTestPage from './pages/TerminalTypesTestPage';
import ChatTestPage from './pages/ChatTestPage';
import AuthDebugger from './components/AuthDebugger';
import WorkspacePage from './pages/WorkspacePage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import AdminTemplatesPage from './pages/AdminTemplatesPage';
import PluginPanelPage from './pages/PluginPanelPage';
import PluginTerminalPage from './pages/PluginTerminalPage';

// Import components
import LoadingSpinner from './components/LoadingSpinner';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

// Import hooks
import { useSystemHealth } from './hooks/useSystemHealth';

function App() {
  const { user, isAuthenticated, isLoading: authLoading, initialize: initializeAuth } = useAuth();
  const { theme, initialize: initializeTheme } = useTheme();
  const { isHealthy, checkHealth } = useSystemHealth();
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize the application
  useEffect(() => {
    async function initializeApp() {
      try {
        // Initialize theme first (synchronous)
        initializeTheme();
        
        // Check system health
        await checkHealth();
        
        // Initialize authentication
        await initializeAuth();
        
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize app:', error);
        setIsInitialized(true); // Still show the app, but with error state
      }
    }

    initializeApp();
  }, []); // 只在应用启动时初始化一次

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  // Show loading screen while initializing
  if (!isInitialized || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <LoadingSpinner size="lg" />
          <div className="space-y-2">
            <h2 className="text-xl font-semibold">Initializing Workspace</h2>
            <p className="text-muted-foreground">
              Setting up your AI development environment...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show system health warning if needed
  if (!isHealthy) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md mx-auto text-center space-y-4 p-6">
          <div className="w-16 h-16 mx-auto bg-destructive/10 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-destructive">System Unavailable</h2>
            <p className="text-muted-foreground">
              The AI Development Workspace is currently experiencing issues. 
              Please try again later or contact your administrator.
            </p>
          </div>
          <button
            onClick={checkHealth}
            className="btn btn-outline btn-sm"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
          }
        />
        
        {/* Protected routes */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard */}
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />

          {/* Workspace routes */}
          <Route path="workspaces" element={<DashboardPage />} />
          <Route path="workspaces/create" element={<CreateWorkspacePage />} />
          <Route path="workspaces/:id" element={<WorkspaceDetailPage />} />
          <Route path="terminal-test" element={<TerminalTestPage />} />
          <Route path="terminal-types-test" element={<TerminalTypesTestPage />} />
          <Route path="chat-test" element={<ChatTestPage />} />
          <Route path="auth-debug" element={<AuthDebugger />} />
          <Route path="workspace/:workspaceId" element={<WorkspacePage />} />
          <Route path="workspace/:workspaceId/session/:sessionId" element={<WorkspacePage />} />
          
          {/* Settings */}
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/:section" element={<SettingsPage />} />
        </Route>

        {/* Admin routes - 独立布局，不套在主Layout里 */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminPage />
            </ProtectedRoute>
          }
        />

        {/* Plugin routes - 不需要 Layout 包装，用于 Appthen iframe 集成 */}
        <Route path="/plugin/panel" element={<PluginPanelPage />} />
        <Route path="/plugin/terminal/:terminalId" element={<PluginTerminalPage />} />

        {/* Catch-all redirect */}
        <Route
          path="*"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          }
        />
      </Routes>
    </div>
  );
}

export default App;
